// FieldDesk — экран Расписание (engineers × days, неделя).
//
// Доработки sprint-расширения:
//   - Цветовое кодирование выездов по type (палитра в styles/theme.ts).
//   - Легенда внизу (5 квадратиков с подписями).
//   - Фильтры в шапке: инженер, типы, «Сегодняшняя неделя».
//   - Клик на ячейку → popover с быстрой инфой + «Открыть полную карточку».

import { useEffect, useMemo, useState } from 'react';
import { addDays, format } from 'date-fns';
import { useAuth } from '../auth/AuthContext';
import type { DayOffDto, EngineerListItem, VisitDto, VisitType } from '../api/types';
import { TYPE_COLOR, TYPE_LABEL, STATUS_SCHEMA } from '../styles/theme';
import { Avatar } from '../components/Avatar';
import { ErrorStrip } from './Dashboard';

interface Props {
  onOpenVisit(v: VisitDto): void;
}

const ALL_TYPES: VisitType[] = ['INSTALLATION', 'TRAINING', 'PAID_VISIT', 'ONLINE'];
const RU_DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export function ScheduleScreen({ onOpenVisit }: Props) {
  const { api } = useAuth();
  const [engineers, setEngineers] = useState<EngineerListItem[]>([]);
  const [visits, setVisits] = useState<VisitDto[]>([]);
  // Спринт 6: выходные дни — Map<userId, Set<YYYY-MM-DD>> для O(1) lookup.
  const [daysOff, setDaysOff] = useState<Map<number, Set<string>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // anchor = первый день окна (сегодня полночь, чтобы addDays давал чистые даты).
  const [anchor, setAnchor] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Фильтры
  const [engineerFilter, setEngineerFilter] = useState<number | null>(null);
  const [typesFilter, setTypesFilter] = useState<VisitType[]>(ALL_TYPES);

  // Popover state — координата якоря и текущий визит.
  const [popover, setPopover] = useState<{ visit: VisitDto; x: number; y: number } | null>(null);

  // Скользящие 7 дней: anchor — первый день (обычно сегодня).
  // Нет привязки к понедельнику — Today всегда первая колонка.
  const week = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(anchor, i));
  }, [anchor]);

  useEffect(() => {
    setLoading(true);
    const from = format(week[0]!, 'yyyy-MM-dd');
    const to = format(week[6]!, 'yyyy-MM-dd');
    Promise.all([api.listEngineers(), api.listVisits({ from, to, limit: 500 })])
      .then(async ([engs, vs]) => {
        setEngineers(engs);
        setVisits(vs.items);
        // Дополнительно тянем days-off для всех ВИДИМЫХ инженеров за окно.
        // GET /users/:id/days-off доступен всем ролям — серые ячейки выходных
        // видят ENGINEER/DISPATCHER/ADMIN. Ошибки не глотаем: проброс в общий
        // .catch покажет ErrorStrip.
        const visible = engs.filter((e) => e.isVisibleInSchedule);
        const map = new Map<number, Set<string>>();
        await Promise.all(
          visible.map(async (e) => {
            const r = await api.listDaysOff(e.id, { from, to });
            map.set(e.id, new Set(r.items.map((d) => d.date)));
          }),
        );
        setDaysOff(map);
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [api, week]);

  // Закрытие popover'а по клику вне.
  useEffect(() => {
    if (!popover) return;
    const close = () => setPopover(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [popover]);

  // Спринт 6: показываем только инженеров с isVisibleInSchedule=true.
  // Если ADMIN явно выбрал инженера в фильтре — показываем независимо от
  // флага (он может смотреть отдельно «скрытого» в персональном фильтре).
  const visibleEngineers = useMemo(
    () => engineers.filter((e) => e.isVisibleInSchedule),
    [engineers],
  );
  const filteredEngineers = useMemo(
    () =>
      engineerFilter
        ? engineers.filter((e) => e.id === engineerFilter)
        : visibleEngineers,
    [engineers, visibleEngineers, engineerFilter],
  );

  const byEngDay = useMemo(() => {
    const map = new Map<string, VisitDto[]>();
    for (const v of visits) {
      if (!typesFilter.includes(v.type)) continue;
      if (engineerFilter && v.engineer.id !== engineerFilter) continue;
      const key = `${v.engineer.id}|${v.scheduledDate}`;
      const arr = map.get(key) ?? [];
      arr.push(v);
      map.set(key, arr);
    }
    return map;
  }, [visits, typesFilter, engineerFilter]);

  function toggleType(t: VisitType) {
    setTypesFilter((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  }

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Расписание</h1>
        <div style={{ display: 'flex', gap: 6 }}>
          <NavBtn onClick={() => setAnchor(addDays(anchor, -1))}>←</NavBtn>
          <NavBtn onClick={() => { const t = new Date(); t.setHours(0,0,0,0); setAnchor(t); }}>Сегодня</NavBtn>
          <NavBtn onClick={() => setAnchor(addDays(anchor, 1))}>→</NavBtn>
        </div>
      </div>

      {/* Фильтры */}
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 12,
          marginBottom: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
          display: 'flex',
          gap: 14,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Инженер</span>
          <select
            value={engineerFilter ?? ''}
            onChange={(e) => setEngineerFilter(e.target.value ? Number(e.target.value) : null)}
            style={{
              padding: '6px 10px',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              fontSize: 13,
              background: '#fff',
              minWidth: 180,
            }}
          >
            <option value="">Все инженеры</option>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>
                {e.fullName}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Типы</span>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {ALL_TYPES.map((t) => {
              const active = typesFilter.includes(t);
              const c = TYPE_COLOR[t] ?? '#64748b';
              return (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  style={{
                    padding: '5px 10px',
                    fontSize: 11,
                    border: `1px solid ${active ? c : '#e2e8f0'}`,
                    background: active ? c + '18' : '#fff',
                    color: active ? c : '#64748b',
                    fontWeight: 600,
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  {TYPE_LABEL[t]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {err && <ErrorStrip message={err} />}

      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `200px repeat(7, minmax(140px, 1fr))`,
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc',
          }}
        >
          <HeadCell>Инженер</HeadCell>
          {week.map((d) => (
            <HeadCell
              key={d.toISOString()}
              highlight={format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')}
            >
              {/* (getDay()+6)%7 переводит JS-воскресенье(0) → 6, понедельник(1) → 0 и т.д. */}
              {RU_DAYS[(d.getDay() + 6) % 7]} {format(d, 'd MMM')}
            </HeadCell>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>Загрузка…</div>
        ) : filteredEngineers.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
            Нет инженеров под выбранный фильтр.
          </div>
        ) : (
          filteredEngineers.map((e) => (
            <div
              key={e.id}
              style={{
                display: 'grid',
                gridTemplateColumns: `200px repeat(7, minmax(140px, 1fr))`,
                borderBottom: '1px solid #f1f5f9',
                minHeight: 90,
              }}
            >
              <div
                style={{
                  padding: '14px 16px',
                  borderRight: '1px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Avatar initials={e.initials} color={e.color} size={28} />
                <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>{e.fullName}</div>
              </div>
              {week.map((d) => {
                const dateStr = format(d, 'yyyy-MM-dd');
                const cell = byEngDay.get(`${e.id}|${dateStr}`) ?? [];
                const isDayOff = daysOff.get(e.id)?.has(dateStr) === true;
                return (
                  <div
                    key={dateStr}
                    title={isDayOff ? 'Выходной день' : undefined}
                    style={{
                      padding: 6,
                      borderRight: '1px solid #f1f5f9',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      // Спринт 6: выходные дни — серая заливка + striped pattern
                      // (визуально отличается от обычной «нет визитов» ячейки).
                      background: isDayOff
                        ? 'repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 6px, #f1f5f9 6px, #f1f5f9 12px)'
                        : undefined,
                      position: 'relative',
                    }}
                  >
                    {isDayOff && cell.length === 0 && (
                      <div
                        style={{
                          fontSize: 10,
                          color: '#64748b',
                          fontWeight: 600,
                          textAlign: 'center',
                          marginTop: 18,
                        }}
                      >
                        🏖 Выходной
                      </div>
                    )}
                    {cell.map((v) => (
                      <MiniCard
                        key={v.id}
                        v={v}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                          setPopover({ visit: v, x: rect.left + rect.width / 2, y: rect.bottom });
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <Legend />

      {popover && (
        <SchedulePopover
          visit={popover.visit}
          x={popover.x}
          y={popover.y}
          onClose={() => setPopover(null)}
          onOpenFull={(v) => {
            setPopover(null);
            onOpenVisit(v);
          }}
        />
      )}
    </div>
  );
}

function MiniCard({ v, onClick }: { v: VisitDto; onClick(ev: React.MouseEvent<HTMLButtonElement>): void }) {
  const color = TYPE_COLOR[v.type] ?? '#64748b';
  const status = STATUS_SCHEMA[v.status];
  return (
    <button
      onClick={onClick}
      title={`${TYPE_LABEL[v.type]} — ${v.client?.name ?? (v.bitrixDealId ? `Сделка #${v.bitrixDealId}` : 'Без клиента')} · ${v.scheduledStart}–${v.scheduledEnd}`}
      style={{
        background: color + '18',
        border: 'none',
        borderLeft: `3px solid ${color}`,
        borderRadius: 6,
        padding: '6px 8px',
        textAlign: 'left',
        color: '#0f172a',
        fontSize: 11,
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {v.scheduledStart} · {v.client?.shortName ?? v.client?.name ?? (v.bitrixDealId ? `#${v.bitrixDealId}` : TYPE_LABEL[v.type])}
      </div>
      <div style={{ color: status?.color ?? '#64748b', fontSize: 10, fontWeight: 500 }}>
        {status?.label ?? v.status}
      </div>
    </button>
  );
}

function Legend() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        marginTop: 14,
        padding: 12,
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Легенда:</span>
      {ALL_TYPES.map((t) => (
        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-block',
              width: 14,
              height: 14,
              borderRadius: 3,
              background: TYPE_COLOR[t] ?? '#64748b',
            }}
          />
          <span style={{ fontSize: 12, color: '#475569' }}>{TYPE_LABEL[t]}</span>
        </div>
      ))}
    </div>
  );
}

// --- Popover для быстрой инфы --------------------------------------------
function SchedulePopover({
  visit,
  x,
  y,
  onClose,
  onOpenFull,
}: {
  visit: VisitDto;
  x: number;
  y: number;
  onClose(): void;
  onOpenFull(v: VisitDto): void;
}) {
  // Позиционирование: центрируем над якорем, прижимаем к экрану.
  const W = 280;
  const left = Math.max(8, Math.min(window.innerWidth - W - 8, x - W / 2));
  const top = Math.min(window.innerHeight - 200, y + 6);
  const color = TYPE_COLOR[visit.type] ?? '#64748b';
  const status = STATUS_SCHEMA[visit.status];

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        left,
        top,
        width: W,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        boxShadow: '0 12px 28px rgba(15,23,42,0.18)',
        zIndex: 9999,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span
          style={{
            padding: '2px 7px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
            background: color + '18',
            color,
          }}
        >
          {TYPE_LABEL[visit.type] ?? visit.typeLabel}
        </span>
        {status && (
          <span
            style={{
              padding: '2px 7px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 600,
              background: status.bg,
              color: status.color,
            }}
          >
            {status.label}
          </span>
        )}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
        {visit.client?.name ?? (visit.bitrixDealId ? `Сделка #${visit.bitrixDealId}` : 'Без клиента')}
      </div>

      <PopRow icon="📅">
        {visit.scheduledDate} · {visit.scheduledStart}–{visit.scheduledEnd}
      </PopRow>
      {visit.arrivedAt && <PopRow icon="✅">Прибыл: {formatTimeLocal(visit.arrivedAt)}</PopRow>}
      {visit.client?.address && <PopRow icon="📍">{visit.client.address}</PopRow>}
      <PopRow icon="👤">{visit.engineer.fullName}</PopRow>

      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        <button
          onClick={() => onOpenFull(visit)}
          style={{
            flex: 1,
            padding: '7px 10px',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Открыть полную карточку
        </button>
        <button
          onClick={onClose}
          style={{
            padding: '7px 10px',
            background: '#fff',
            color: '#475569',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}

function PopRow({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>
      <span style={{ marginRight: 6 }}>{icon}</span>
      {children}
    </div>
  );
}

function formatTimeLocal(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function HeadCell({ children, highlight }: { children: React.ReactNode; highlight?: boolean }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        fontSize: 12,
        fontWeight: 600,
        color: highlight ? '#2563eb' : '#64748b',
        borderRight: '1px solid #e2e8f0',
        background: highlight ? '#eff6ff' : undefined,
      }}
    >
      {children}
    </div>
  );
}

function NavBtn({ children, onClick }: { children: React.ReactNode; onClick(): void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '7px 12px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        fontSize: 12,
        color: '#475569',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
